import React, { useEffect, useId, useMemo } from 'react'
import { SelectPicker } from 'rsuite'
import { PickerHandle } from 'rsuite/esm/internals/Picker'
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree'
import styled from 'styled-components'
import { DropdownOption } from 'types'

import {
  InputAndSubtitle,
  sharedPickerFocusStyles,
  StyledInputContainer,
  StyledLabel,
  SubtitleRowStyled,
  SubtitleTextStyled,
} from './InputStyles'

export interface DropdownProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $itemKey?: string
  $ariaLabelledBy?: string
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  units?: string
  options: DropdownOption[]
  onValueChange: (value: string) => void
  onClean?: () => void
  value?: string
  defaultValue?: string
  placeholder?: string
  mode?: 'read' | 'edit'
  searchable?: boolean
  cleanable?: boolean
  disabled?: boolean
  renderMenuItem?: (
    label: React.ReactNode,
    item: ItemDataType
  ) => React.ReactNode
  menuMaxHeight?: number
  renderValue?: (
    value: string,
    item: ItemDataType<string>,
    selectedElement: React.ReactNode
  ) => React.ReactNode
}

const PickerWrapper = styled.div<{
  $backgroundColor?: string
  $borderColor?: string
}>`
  position: relative;
  width: 100%;

  ${sharedPickerFocusStyles}

  .rs-picker {
    width: 100%;
  }

  .rs-picker-default .rs-picker-toggle.rs-btn {
    padding: 0.25rem;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important;
    border-color: ${({ $borderColor }) =>
      $borderColor || 'transparent'} !important;
    border-radius: 0.25rem;
  }

  .rs-btn {
    &:hover {
      background-color: ${({ $backgroundColor, theme }) =>
        $backgroundColor || theme.forecast.hoverGhostButton} !important;
    }
  }

  .rs-picker-toggle-value {
    padding-left: 0.25rem;
  }

  .rs-picker-clean {
    &:focus {
      outline: 1px solid ${({ theme }) => theme.forecast.border} !important;
    }
  }

  .rs-picker-toggle-placeholder {
    color: #888 !important;
    padding-left: 0.25rem;
    font-size: 12px !important;
  }
`

const ReadOnlyDiv = styled.div`
  display: flex;
  align-items: center;
  padding: var(--Padding-1_5, 6px) var(--Padding-1, 4px);
  color: ${({ theme }) => theme.forecast.defaultText};
`

export const Dropdown = ({
  options,
  onValueChange,
  value,
  defaultValue = '',
  onClean,
  $label,
  $itemKey,
  $ariaLabelledBy,
  $labelColor,
  $labelLocation,
  $subtitleText,
  $subtitleTextColor,
  $backgroundColor,
  $borderColor,
  mode,
  cleanable = true,
  placeholder,
  searchable = true,
  disabled,
  renderMenuItem,
  renderValue,
  menuMaxHeight,
}: DropdownProps) => {
  const dropdownContainerRef = React.useRef<HTMLDivElement>(null)
  const pickerRef = React.useRef<PickerHandle>(null)
  const [selectedValue, setSelectedValue] = React.useState<
    string | null | undefined
  >((value ?? defaultValue) || null)
  const toggleOption = (toggledOption: string) => {
    const filteredOption = options?.find(
      (option) => option.value === toggledOption
    )
    if (filteredOption !== undefined) {
      setSelectedValue(filteredOption.value)
      onValueChange(toggledOption)
    }
  }

  const renderReadOnlyValue = useMemo(() => {
    const filteredOption = options?.find(
      (option) => option.value === defaultValue
    )
    return filteredOption?.label
  }, [defaultValue, options])

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value || null)
      return
    }
    if (mode === 'read') {
      const filteredOption = options?.find(
        (option) => option.value === defaultValue
      )
      if (filteredOption !== undefined) {
        setSelectedValue(filteredOption.value)
      }
    }
  }, [mode, options, defaultValue, setSelectedValue, value])

  const removeOption = () => {
    setSelectedValue(null)
    onValueChange('')
  }

  // The popup is portaled to document.body so an ancestor with `overflow:
  // hidden` can't clip it. A unique class lets us find this picker's menu
  // among any others in the DOM.
  const menuClassName = `dropdown-menu-${useId().replace(/:/g, '')}`

  // Since the menu no longer lives next to the toggle in the DOM, it can't be
  // reached by Tab. Instead we move focus into the search box when the menu
  // opens, so keyboard users can immediately type to filter / arrow to select.
  const focusSearchInput = () => {
    document
      .querySelector<HTMLInputElement>(`.${menuClassName} .rs-search-box-input`)
      ?.focus()
  }

  useEffect(() => {
    const container = dropdownContainerRef.current
    if (!container || mode === 'read') return

    // rsuite hardcodes tabindex="-1" on the clean (clear) button, so it can't
    // be reached by keyboard. It renders conditionally (only when there's a
    // value), so we re-apply tabindex="0" whenever it (re)appears.
    const makeCleanButtonFocusable = () => {
      container
        .querySelectorAll<HTMLElement>('.rs-picker-clean')
        .forEach((element) => {
          if (element.getAttribute('tabindex') !== '0') {
            element.setAttribute('tabindex', '0')
          }
        })
    }

    makeCleanButtonFocusable()

    const observer = new MutationObserver(makeCleanButtonFocusable)
    observer.observe(container, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [mode])

  // The popup is portaled to the end of <body>, so once focus is inside it,
  // native Tab would escape to the browser chrome. We trap focus and route it
  // manually: search box -> each option -> exit. Exiting forward focuses the
  // clean button (or toggle); Shift+Tab off the search box closes the menu and
  // returns focus to the (closed) toggle.
  useEffect(() => {
    if (mode === 'read') return

    const handleKeyDown = (event: KeyboardEvent) => {
      const menu = document.querySelector<HTMLElement>(`.${menuClassName}`)
      const active = document.activeElement as HTMLElement | null

      // Only manage keys while focus is actually inside this dropdown's menu.
      if (!menu || !active || !menu.contains(active)) return

      const searchInput = menu.querySelector<HTMLElement>(
        '.rs-search-box-input'
      )
      const optionElements = Array.from(
        menu.querySelectorAll<HTMLElement>('[role="option"]')
      )

      // Options default to tabindex="-1"; make the ones we route to focusable.
      optionElements.forEach((option) => {
        if (option.getAttribute('tabindex') !== '0') {
          option.setAttribute('tabindex', '0')
        }
      })

      // Enter/Space on a focused option selects it (rsuite selects on click).
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        active.getAttribute('role') === 'option'
      ) {
        event.preventDefault()
        event.stopPropagation()
        active.click()
        return
      }

      if (event.key !== 'Tab') return

      const stops = [searchInput, ...optionElements].filter(
        (element): element is HTMLElement => element !== null
      )
      const currentIndex = stops.indexOf(active)
      if (currentIndex === -1) return

      // Take over Tab entirely so focus can't leave the menu into the page.
      event.preventDefault()
      event.stopPropagation()

      const container = dropdownContainerRef.current
      const cleanButton =
        container?.querySelector<HTMLElement>('.rs-picker-clean') ?? null
      const toggle =
        pickerRef.current?.target ??
        container?.querySelector<HTMLElement>('.rs-picker-toggle') ??
        null

      if (event.shiftKey) {
        if (currentIndex === 0) {
          pickerRef.current?.close?.()
          toggle?.focus()
        } else {
          stops[currentIndex - 1].focus()
        }
        return
      }

      if (currentIndex === stops.length - 1) {
        pickerRef.current?.close?.()
        ;(cleanButton ?? toggle)?.focus()
      } else {
        stops[currentIndex + 1].focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [mode, menuClassName])

  return (
    <StyledInputContainer $labelLocation={$labelLocation}>
      {$label && (
        <StyledLabel $labelColor={$labelColor} htmlFor={$label}>
          {$label}
        </StyledLabel>
      )}

      <PickerWrapper
        ref={dropdownContainerRef}
        $backgroundColor={$backgroundColor}
        $borderColor={$borderColor}>
        <InputAndSubtitle>
          {mode === 'read' ? (
            <ReadOnlyDiv>{renderReadOnlyValue}</ReadOnlyDiv>
          ) : (
            <>
              <SelectPicker
                ref={pickerRef}
                cleanable={cleanable}
                data-testid={`custom-form-field-${$label}`}
                id={$itemKey}
                aria-label={
                  $ariaLabelledBy
                    ? undefined
                    : $label
                      ? `Select ${$label}`
                      : undefined
                }
                aria-labelledby={$ariaLabelledBy ?? $itemKey}
                name={$itemKey}
                onChange={(e) => {
                  if (e) {
                    toggleOption(e)
                  }
                }}
                onClean={onClean ?? removeOption}
                searchable={searchable}
                value={selectedValue}
                data={options ?? []}
                aria-required='true'
                placeholder={placeholder}
                menuClassName={menuClassName}
                menuMaxHeight={menuMaxHeight}
                onEntered={focusSearchInput}
                disabled={disabled}
                renderMenuItem={renderMenuItem}
                renderValue={renderValue}
                virtualized={false}
              />
            </>
          )}
          {mode !== 'read' && $subtitleText && (
            <SubtitleRowStyled>
              <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
                {$subtitleText}
              </SubtitleTextStyled>
            </SubtitleRowStyled>
          )}
        </InputAndSubtitle>
      </PickerWrapper>
    </StyledInputContainer>
  )
}

Dropdown.displayName = 'Dropdown'
